import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";
import {
  Sparkles, Volume2, Languages, Brain, Target, GraduationCap, Check,
  ArrowRight, BookOpen, PenLine, Rocket, ShieldCheck, Star, Play,
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { API_TIMEOUT } from "../utils/crawler";

const INK = "#0B1020";
const INDIGO = "#4F46E5";
const CYAN = "#06B6D4";

// CSS-indtoning, ikke framer: en scroll-udløst framer-animation skriver inline
// `opacity: 0`, og prerenderingen snapshotter siden, før den er kørt.
const Reveal = ({ children, delay = 0, className = "" }) => (
  <div className={`votra-rise ${className}`} style={delay ? { animationDelay: `${delay}s` } : undefined}>
    {children}
  </div>
);

const FvuLanding = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", package: "plus", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/fvu/config`, { timeout: API_TIMEOUT }).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSending(true);
    try {
      await axios.post(`${API}/fvu/request-access`, form);
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Noget gik galt, prøv igen.");
    } finally { setSending(false); }
  };

  const packages = cfg?.packages || [];
  const modules = cfg?.modules || [];

  const HERO_STATS = [
    { n: "12", l: "moduler" }, { n: "60", l: "timer" },
    { n: "3", l: "øveprøver" }, { n: "12", l: "sprog" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: INK }}>
        <div className="absolute inset-0 opacity-[0.5]" aria-hidden style={{
          background: `radial-gradient(60% 60% at 15% 10%, ${INDIGO}44, transparent), radial-gradient(50% 50% at 90% 20%, ${CYAN}33, transparent)`,
        }} />
        <div className="absolute inset-0 opacity-[0.08]" aria-hidden style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} />
        <div className="relative container mx-auto px-5 md:px-6 pt-20 pb-24 max-w-5xl text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-cyan-200" data-testid="fvu-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> Votra · FVU-Dansk trin 2
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 text-white font-bold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Bliv klar til FVU-dansk trin 2 <br className="hidden sm:block" />
              med en <span style={{ background: `linear-gradient(120deg, ${CYAN}, #a5b4fc)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI-tutor på dit sprog</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 mx-auto max-w-2xl text-slate-300 text-base md:text-lg">
              12 moduler, oplæsning på alt indhold, ordforklaring på 12 sprog og realistiske øveprøver.
              Danmarks mest moderne FVU-forberedelse, øv når du vil, i dit eget tempo.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/fvu/app?demo=1"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white no-underline shadow-lg transition-transform hover:scale-[1.03]"
                style={{ background: `linear-gradient(120deg, ${INDIGO}, ${CYAN})` }}
                data-testid="fvu-try-demo-btn"
              >
                <Play className="h-5 w-5" /> Prøv gratis demo
              </Link>
              <a href="#pakker"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white border border-white/20 bg-white/[0.05] hover:bg-white/[0.1] transition-colors"
                data-testid="fvu-see-packages-btn">
                Se pakker <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/fvu/app"
                className="text-slate-300 hover:text-white text-sm font-medium underline underline-offset-4"
                data-testid="fvu-login-link"
              >
                Har du en licens? Log ind
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="mt-14 grid grid-cols-4 gap-4 max-w-xl mx-auto">
              {HERO_STATS.map((s) => (
                <div key={s.l} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white">{s.n}</div>
                  <div className="text-xs md:text-sm text-slate-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW / FEATURES */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container mx-auto px-5 md:px-6 max-w-6xl">
          <Reveal className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: INDIGO }}>Sådan arbejder du</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold" style={{ color: INK }}>Lær → Øv → Anvend, med hjælp hele vejen</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, t: "Lær", d: "Læs forklaringen og se eksemplerne, eller tryk på Lyt og hør det hele med dansk stemme." },
              { icon: PenLine, t: "Øv", d: "Løs opgaver med øjeblikkelig feedback. Markér svære ord og få forklaring på dit sprog." },
              { icon: Rocket, t: "Anvend", d: "Brug det lærte i praktiske skriveopgaver fra job, uddannelse og hverdag." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `linear-gradient(120deg, ${INDIGO}, ${CYAN})` }}>
                    <c.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: INK }}>{c.t}</h3>
                  <p className="text-slate-600">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {[
              { icon: Brain, t: "AI-tutor 24/7", d: "Kender hele forløbet og svarer på dine spørgsmål på 12 sprog." },
              { icon: Languages, t: "Ordforklaring", d: "Markér et ord → betydning, udtale og oversættelse med det samme." },
              { icon: Volume2, t: "Oplæsning", d: "Hør alt indhold læst op, perfekt for svage læsere og ordblinde." },
              { icon: Target, t: "Prøve-simulator", d: "Realistiske øveprøver med point, delprøver og AI-skrivevurdering." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-6">
                  <c.icon className="h-6 w-6 mb-3" style={{ color: INDIGO }} />
                  <h4 className="font-bold mb-1.5" style={{ color: INK }}>{c.t}</h4>
                  <p className="text-sm text-slate-600">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-5 md:px-6 max-w-5xl">
          <Reveal className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: INDIGO }}>Indhold</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold" style={{ color: INK }}>12 moduler · 60 timer</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            {modules.map((m) => (
              <Reveal key={m.index} delay={(m.index % 4) * 0.04}>
                <div className="flex gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid={`fvu-module-${m.index}`}>
                  <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white" style={{ background: INK }}>
                    {String(m.index + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: INK }}>{m.title}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{m.intro}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{m.hours} timer</span>
                      {(m.tags || []).map((t) => (
                        <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pakker" className="py-20 md:py-24" style={{ background: INK }}>
        <div className="container mx-auto px-5 md:px-6 max-w-6xl">
          <Reveal className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Licenser</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white">Vælg din adgang</h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">Engangskøb der giver tidsbegrænset adgang. Ingen abonnement. Du får din licensnøgle på mail, når betalingen er bekræftet.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {packages.map((p, i) => {
              const featured = p.id === "plus";
              return (
                <Reveal key={p.id} delay={i * 0.08} className="h-full">
                  <div
                    className={`h-full rounded-2xl p-7 flex flex-col ${featured ? "ring-2" : "border border-white/10"}`}
                    style={{ background: featured ? "linear-gradient(160deg, #1e1b4b, #0b1020)" : "rgba(255,255,255,0.03)", ...(featured ? { boxShadow: `0 20px 60px ${INDIGO}44` } : {}) }}
                    data-testid={`fvu-price-${p.id}`}
                  >
                    {featured && <span className="self-start mb-3 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full text-white" style={{ background: `linear-gradient(120deg, ${INDIGO}, ${CYAN})` }}>{p.tagline}</span>}
                    <h3 className="text-2xl font-bold text-white">{p.name}</h3>
                    <div className="mt-3 flex items-end gap-1">
                      <span className="text-4xl font-bold text-white">{p.price.toLocaleString("da-DK")}</span>
                      <span className="text-slate-400 mb-1">kr.</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{p.days} dages adgang</p>
                    <ul className="mt-6 space-y-3 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-slate-200 text-sm">
                          <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: CYAN }} /> {f}
                        </li>
                      ))}
                    </ul>
                    <a href="#anmod" onClick={() => setForm((s) => ({ ...s, package: p.id }))}
                      className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-transform hover:scale-[1.02] ${featured ? "text-white" : "text-white border border-white/20 bg-white/[0.05] hover:bg-white/[0.1]"}`}
                      style={featured ? { background: `linear-gradient(120deg, ${INDIGO}, ${CYAN})` } : {}}
                      data-testid={`fvu-choose-${p.id}`}>
                      Vælg {p.name} <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="text-center mt-10">
            <Link to="/fvu/app?demo=1" className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold" data-testid="fvu-demo-cta">
              <Play className="h-4 w-4" /> Eller prøv den gratis demo først
            </Link>
          </Reveal>
        </div>
      </section>

      {/* REQUEST ACCESS */}
      <section id="anmod" className="py-20 md:py-24 bg-slate-50">
        <div className="container mx-auto px-5 md:px-6 max-w-2xl">
          <Reveal className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: INDIGO }}>Anmod om adgang</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold" style={{ color: INK }}>Kom i gang</h2>
            <p className="mt-3 text-slate-600">Udfyld formularen. Vi kontakter dig og sender din licensnøgle, når betalingen er bekræftet.</p>
          </Reveal>
          {sent ? (
            <div className="rounded-2xl bg-white border border-emerald-200 p-8 text-center" data-testid="fvu-request-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold" style={{ color: INK }}>Tak for din anmodning!</h3>
              <p className="text-slate-600 mt-2">Vi vender tilbage hurtigst muligt med din licensnøgle.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-7 space-y-4 shadow-sm" data-testid="fvu-request-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Navn *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" data-testid="fvu-req-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Telefon</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" data-testid="fvu-req-phone" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">E-mail *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" data-testid="fvu-req-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Pakke</label>
                <select value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" data-testid="fvu-req-package">
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.name}, ${p.days} dage / ${p.price} kr.`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Besked (valgfri)</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" data-testid="fvu-req-message" />
              </div>
              {err && <p className="text-sm text-red-600" data-testid="fvu-req-error">{err}</p>}
              <button type="submit" disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white disabled:opacity-60"
                style={{ background: `linear-gradient(120deg, ${INDIGO}, ${CYAN})` }} data-testid="fvu-req-submit">
                {sending ? "Sender…" : "Send anmodning"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}
          <div className="mt-8 flex items-start gap-3 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
            <p>Votras Dansk trin 2 er originalt ØVEmateriale bygget efter FVU-bekendtgørelsens læreplan, det er ikke en officiel prøve og giver ikke et officielt prøvebevis.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FvuLanding;
